import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { TimeWindow } from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { MINUTE_STEPS, TIME_FORMAT } from 'src/components/WeekSchedule/const';
import {
  describeScheduleWindows,
  expandScheduleDays,
  SCHEDULE_WEEKDAY_OPTIONS,
  type ScheduleWeekDayOption,
  summarizeScheduleDays,
} from './schedule-windows';

/** Any fixed day: the pickers need a full date, but only its time is read back. */
const CLOCK_REFERENCE_DATE = '2024-01-01';

export interface ScheduleConditionEditorProps {
  windows: TimeWindow[];
  onChange: (windows: TimeWindow[]) => void;
  /** the query builder's `rule-value` class, which places the editor in the row's value cell */
  className?: string;
  /**
   * marks a schedule the form will refuse to save: no day picked, or an end time not after the
   * start time
   */
  markInvalid?: boolean;
}

/**
 * The Custom schedule row: "[days] from [time] to [time]". One time range per condition — another
 * range is another Custom schedule condition, which the builder already adds and removes.
 */
export const ScheduleConditionEditor = ({
  windows,
  onChange,
  className,
  markInvalid = false,
}: ScheduleConditionEditorProps) => {
  const { weekDays, timeFrom, timeTo } = describeScheduleWindows(windows);
  const selectedDays = SCHEDULE_WEEKDAY_OPTIONS.filter((option) =>
    weekDays.includes(option.value),
  );
  const isMissingDays = markInvalid && selectedDays.length === 0;
  const from = toClock(timeFrom);
  const to = toClock(timeTo);
  // the pickers only limit what can be picked; a typed time still arrives through `onChange`
  const isInvertedRange = markInvalid && !to.isAfter(from);

  const emit = (nextWeekDays: readonly number[], nextFrom: string, nextTo: string): void =>
    onChange(expandScheduleDays(nextWeekDays, nextFrom, nextTo));

  const emitTime = (field: 'from' | 'to') => (value: Dayjs | null) => {
    if (!value?.isValid()) {
      return;
    }
    const time = value.format(TIME_FORMAT);
    emit(weekDays, field === 'from' ? time : timeFrom, field === 'to' ? time : timeTo);
  };

  return (
    <Stack className={className} spacing={1}>
      <Autocomplete<ScheduleWeekDayOption, true, true>
        multiple
        disableClearable
        disableCloseOnSelect
        size="small"
        options={SCHEDULE_WEEKDAY_OPTIONS}
        value={selectedDays}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        onChange={(_, selected) =>
          emit(
            selected.map((option) => option.value),
            timeFrom,
            timeTo,
          )
        }
        // the trigger stays one line: named days up to two, then a count
        renderTags={(selected) => (
          <Typography variant="body2" sx={{ px: 0.5 }}>
            {summarizeScheduleDays(selected.map((option) => option.value))}
          </Typography>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedDays.length ? undefined : 'Days'}
            error={isMissingDays}
            helperText={isMissingDays ? 'Select at least one day' : undefined}
          />
        )}
      />
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {/* the boxes let the pickers shrink to share the value cell instead of wrapping */}
        <Box sx={{ flex: '1 1 7.5rem', minWidth: '7.5rem' }}>
          <TimePicker
            label="From"
            value={from}
            maxTime={to.subtract(MINUTE_STEPS, 'minute')}
            timeSteps={{ minutes: MINUTE_STEPS }}
            onChange={emitTime('from')}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>
        <Box sx={{ flex: '1 1 7.5rem', minWidth: '7.5rem' }}>
          <TimePicker
            label="To"
            value={to}
            minTime={from.add(MINUTE_STEPS, 'minute')}
            timeSteps={{ minutes: MINUTE_STEPS }}
            onChange={emitTime('to')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: isInvertedRange,
                helperText: isInvertedRange ? 'Must be after From' : undefined,
              },
            }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

function toClock(time: string): Dayjs {
  return dayjs(`${CLOCK_REFERENCE_DATE}T${time}`);
}
